import { useMemo } from 'react';

import { Box } from '@mui/material';
import { Form, Formik, FormikConfig } from 'formik';
import type { InferType, BaseSchema } from 'yup';

import type { DeepPartial, Override, TSxStyles } from '@/types/common';

type TFormikFormProps<PSchema extends BaseSchema, PValues = InferType<PSchema>> = Override<
  FormikConfig<PValues>,
  {
    validationSchema: PSchema;
    initialValues?: DeepPartial<PValues>;
    sx?: TSxStyles;
  }
>;

const getErrors = <PSchema extends BaseSchema>(schema: PSchema, values: InferType<PSchema>) => {
  try {
    schema.validateSync(values);
  } catch (error) {
    return { [error.path]: error.message };
  }

  return {};
};

/**
 * Formik form with the portal's validation set-up.
 * Start values: `initialValues` are cast and pre-validated once (`initialErrors`), and the form starts
 * from them when it mounts.
 * Re-initialising: OFF by default (`enableReinitialize={false}`). Server data refreshes in the
 * background (React Query), and a refresh that brings changed data must never reset what the
 * operator is typing. A form that edits server data must therefore:
 * - take a `key` on the entity id, so opening another entity builds a fresh form, and
 * - call `resetForm({ values: saved, errors: {} })` after a successful save, so Save disables and
 *   no old error stays (formik's `resetForm({ values })` alone keeps the mount-time errors).
 * Keep `initialValues` content-stable: formik revalidates when their content changes (`validateOnMount`).
 * A caller that really needs re-initialising passes `enableReinitialize` itself (it overrides this
 * default, because `...props` is spread last).
 * See `.knowledge/rules/formik-patterns.md`.
 */
export const FormikForm = <PSchema extends BaseSchema>({
  validationSchema,
  initialValues,
  children,
  sx,
  ...props
}: TFormikFormProps<PSchema>) => {
  const initial = useMemo(
    () => ({
      values: validationSchema.cast(initialValues ?? {}),
      errors: getErrors(validationSchema, initialValues ?? {}),
    }),
    [validationSchema, initialValues],
  );

  return (
    <Formik
      initialValues={initial.values}
      initialErrors={initial.errors}
      validationSchema={validationSchema}
      enableReinitialize={false}
      validateOnMount
      {...props}
    >
      <Box component={Form} sx={sx}>
        {children}
      </Box>
    </Formik>
  );
};
